import { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'
import {
  Button,
  PageLoading,
  PageHeader,
  FilterBar,
  SearchFilter,
  Table,
  Badge,
  Card,
  NoOrganizationMessage,
} from '../components'
import type { Invoice } from '../api/types'

interface OverdueInvoice extends Invoice {
  reminderCount: number
  lastReminderSentAt: string | null
  totalReminderFees: number
}

interface OverdueSummary {
  count: number
  totalAmount: number
}

interface Column<T> {
  key: string
  header: string
  render?: (item: T) => React.ReactNode
  align?: 'left' | 'center' | 'right'
  width?: string
}

// Format currency
function formatEuro(cents: number): string {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100)
}

// Format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('fi-FI')
}

// Calculate days overdue
function getDaysOverdue(dueDate: string): number {
  const due = new Date(dueDate)
  const today = new Date()
  const diff = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))
  return Math.max(0, diff)
}

// Get status badge
function getStatusBadge(invoice: OverdueInvoice) {
  const daysOverdue = getDaysOverdue(invoice.dueDate)
  if (daysOverdue > 30) {
    return <Badge variant="error">{daysOverdue} pv myöhässä</Badge>
  } else if (daysOverdue > 14) {
    return <Badge variant="warning">{daysOverdue} pv myöhässä</Badge>
  }
  return <Badge variant="neutral">{daysOverdue} pv myöhässä</Badge>
}

export default function OverdueInvoicesPage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const [invoices, setInvoices] = useState<OverdueInvoice[]>([])
  const [summary, setSummary] = useState<OverdueSummary>({ count: 0, totalAmount: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  const [sendingBatch, setSendingBatch] = useState(false)

  // Fetch overdue invoices
  const fetchInvoices = useCallback(async () => {
    if (!activeOrg) return
    setLoading(true)
    setError('')
    try {
      const data = await apiClient.get<{
        invoices: OverdueInvoice[]
        summary: OverdueSummary
      }>(`/invoices?organization_id=${activeOrg.id}&status=overdue`)
      setInvoices(data.invoices || [])
      setSummary(data.summary || { count: 0, totalAmount: 0 })
    } catch {
      setError('Erääntyneiden laskujen lataus epäonnistui')
    } finally {
      setLoading(false)
    }
  }, [activeOrg])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(
      (inv) =>
        filter === '' ||
        inv.customerEmail.toLowerCase().includes(filter.toLowerCase()) ||
        inv.customerName?.toLowerCase().includes(filter.toLowerCase()) ||
        inv.invoiceNumber.toLowerCase().includes(filter.toLowerCase())
    )
  }, [invoices, filter])

  // Send reminder for single invoice
  const handleRemind = async (invoiceId: string) => {
    if (!confirm('Lähetetäänkö maksumuistutus?')) return
    try {
      await apiClient.post(`/invoices/${invoiceId}/remind`, {})
      await fetchInvoices()
    } catch {
      setError('Muistutuksen lähetys epäonnistui')
    }
  }

  // Send batch reminders
  const handleBatchRemind = async () => {
    if (!confirm(`Lähetetäänkö maksumuistutukset ${summary.count} laskulle?`)) return
    setSendingBatch(true)
    try {
      const result = await apiClient.post<{ sent: number; failed: number }>(
        '/invoices/send-batch-reminders',
        { organization_id: activeOrg?.id }
      )
      alert(`Muistutukset lähetetty: ${result.sent} onnistui, ${result.failed} epäonnistui`)
      await fetchInvoices()
    } catch {
      setError('Erän lähetys epäonnistui')
    } finally {
      setSendingBatch(false)
    }
  }

  // Table columns with render functions
  const columns: Column<OverdueInvoice>[] = [
    { 
      key: 'invoiceNumber', 
      header: 'Laskunro', 
      width: 'w-32',
      render: (inv: OverdueInvoice) => <span className="font-medium">{inv.invoiceNumber}</span>
    },
    { 
      key: 'customer', 
      header: 'Asiakas', 
      width: 'w-48',
      render: (inv: OverdueInvoice) => (
        <div>
          <div className="font-medium">{inv.customerName || '—'}</div>
          <div className="text-sm text-gray-500">{inv.customerEmail}</div>
        </div>
      )
    },
    { 
      key: 'dueDate', 
      header: 'Eräpäivä', 
      width: 'w-28',
      render: (inv: OverdueInvoice) => (
        <div>
          <div>{formatDate(inv.dueDate)}</div>
          <div className="text-sm text-red-600">
            {getDaysOverdue(inv.dueDate)} pv myöhässä
          </div>
        </div>
      )
    },
    { 
      key: 'amount', 
      header: 'Summa', 
      width: 'w-28', 
      align: 'right',
      render: (inv: OverdueInvoice) => {
        const totalWithFees = inv.totalAmount + (inv.totalReminderFees || 0)
        return (
          <div className="text-right">
            <div className="font-medium">{formatEuro(totalWithFees)}</div>
            {(inv.totalReminderFees || 0) > 0 && (
              <div className="text-xs text-gray-500">
                (sis. {formatEuro(inv.totalReminderFees)} muistutusmaksuja)
              </div>
            )}
          </div>
        )
      }
    },
    { 
      key: 'reminders', 
      header: 'Muistutukset', 
      width: 'w-28',
      render: (inv: OverdueInvoice) => (
        <div>
          <div>{inv.reminderCount || 0} kpl</div>
          {inv.lastReminderSentAt && (
            <div className="text-xs text-gray-500">
              Viim. {formatDate(inv.lastReminderSentAt)}
            </div>
          )}
        </div>
      )
    },
    { 
      key: 'status', 
      header: 'Tila', 
      width: 'w-32',
      render: (inv: OverdueInvoice) => getStatusBadge(inv)
    },
    { 
      key: 'actions', 
      header: '', 
      width: 'w-32',
      render: (inv: OverdueInvoice) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleRemind(inv.id)}
            title="Lähetä muistutus"
          >
            📧
          </Button>
        </div>
      )
    },
  ]

  if (!activeOrg) {
    return <NoOrganizationMessage />
  }

  if (loading) return <PageLoading />

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Erääntyneet laskut"
        subtitle={`${activeOrg.name} • ${summary.count} kpl, yhteensä ${formatEuro(summary.totalAmount)}`}
      />

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Erääntyneitä laskuja" className="bg-red-50">
          <div className="text-3xl font-bold text-red-600">{summary.count}</div>
        </Card>
        <Card title="Yhteissumma" className="bg-red-50">
          <div className="text-3xl font-bold text-red-600">
            {formatEuro(summary.totalAmount)}
          </div>
        </Card>
        <Card title="Toiminnot">
          <Button
            onClick={handleBatchRemind}
            disabled={sendingBatch || summary.count === 0}
            variant="primary"
          >
            {sendingBatch ? 'Lähetetään...' : 'Lähetä kaikki muistutukset'}
          </Button>
        </Card>
      </div>

      {/* Filters */}
      <FilterBar>
        <SearchFilter
          value={filter}
          onChange={setFilter}
          placeholder={t('overdueInvoices.searchByCustomerOrInvoice')}
        />
      </FilterBar>

      {/* Table */}
      <Table
        columns={columns}
        data={filteredInvoices}
        keyExtractor={(inv: OverdueInvoice) => inv.id}
        emptyMessage={
          filter
            ? 'Ei hakua vastaavia erääntyneitä laskuja'
            : 'Ei erääntyneitä laskuja 🎉'
        }
      />
    </div>
  )
}
